import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';

/**
 * ThemeToggleButton - A clean, standalone theme toggle component
 * 
 * Features:
 * - Inline SVG icons with explicit colors (no CSS inheritance issues)
 * - Proper accessibility with ARIA labels
 * - Supports both desktop (icon only) and mobile (icon + text) variants
 * - Uses hardcoded colors that match the design system
 */

// Sun icon SVG paths (shown in dark mode to switch to light)
const SunIcon = ({ size = 20, color = "#f8fafc" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    style={{ display: 'block' }}
  >
    <circle cx="12" cy="12" r="4" stroke={color} strokeWidth="2" />
    <path d="M12 2v2" stroke={color} strokeWidth="2" strokeLinecap="round" />
    <path d="M12 20v2" stroke={color} strokeWidth="2" strokeLinecap="round" />
    <path d="M4.93 4.93l1.41 1.41" stroke={color} strokeWidth="2" strokeLinecap="round" />
    <path d="M17.66 17.66l1.41 1.41" stroke={color} strokeWidth="2" strokeLinecap="round" />
    <path d="M2 12h2" stroke={color} strokeWidth="2" strokeLinecap="round" />
    <path d="M20 12h2" stroke={color} strokeWidth="2" strokeLinecap="round" />
    <path d="M6.34 17.66l-1.41 1.41" stroke={color} strokeWidth="2" strokeLinecap="round" />
    <path d="M19.07 4.93l-1.41 1.41" stroke={color} strokeWidth="2" strokeLinecap="round" />
  </svg>
);

// Moon icon SVG paths (shown in light mode to switch to dark)
const MoonIcon = ({ size = 20, color = "#0f172a" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    style={{ display: 'block' }}
  >
    <path 
      d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" 
      stroke={color} 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round" 
    />
  </svg>
);

/**
 * Desktop Theme Toggle Button
 * - Icon only, square button
 * - 40x40px touch target
 */
export const ThemeToggleDesktop = () => {
  const { isDark, toggleLightDark } = useTheme();
  
  return (
    <button
      type="button"
      onClick={toggleLightDark}
      className="hidden md:flex items-center justify-center w-10 h-10 rounded-lg border transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2"
      style={{
        backgroundColor: isDark ? '#334155' : '#f1f5f9',
        borderColor: isDark ? '#475569' : '#e2e8f0',
      }}
      aria-label={`Switch to ${isDark ? 'light' : 'dark'} mode`}
      title={`Switch to ${isDark ? 'light' : 'dark'} mode`}
    >
      {isDark ? (
        <SunIcon size={20} color="#f8fafc" />
      ) : (
        <MoonIcon size={20} color="#0f172a" />
      )}
    </button>
  );
};

/**
 * Mobile Theme Toggle Button
 * - Icon + text label
 * - Full width touch target
 */
export const ThemeToggleMobile = () => {
  const { isDark, theme, toggleTheme } = useTheme();
  
  return (
    <button
      type="button"
      onClick={toggleTheme}
      className="flex items-center space-x-3 w-full py-2 rounded-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2"
      style={{
        color: isDark ? '#f8fafc' : '#0f172a',
      }}
      aria-label={`Switch to ${isDark ? 'light' : 'dark'} theme. Currently using ${theme} theme.`}
    >
      {isDark ? (
        <SunIcon size={20} color="#f8fafc" />
      ) : (
        <MoonIcon size={20} color="#0f172a" />
      )}
      <span className="font-medium">
        {isDark ? 'Light Mode' : 'Dark Mode'}
      </span>
    </button>
  );
};

export default ThemeToggleDesktop;
